import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import authService from '../../services/authService';
import { getApiErrorMessage, logError } from '../../utils/apiError';
import AdminHeader from '../../components/admin/AdminHeader';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('UsersPage - Component mounted');
    
    // Check if user is logged in as admin using authService
    if (!authService.isAdminLoggedIn()) {
      console.log('UsersPage - Not authenticated, redirecting to login');
      navigate('/admin/login');
      return;
    }
    
    // Get admin user data from authService
    const userData = authService.getAdminUser();
    
    if (!userData) {
      console.log('UsersPage - No admin user data, redirecting to login');
      navigate('/admin/login');
      return;
    }
    
    console.log('UsersPage - User data:', userData);
    setAdminUser(userData);
  }, [navigate]);

  useEffect(() => {
    if (!adminUser) return;
    
    console.log('UsersPage - Fetching users');
    setLoading(true);
    
    api.getUsers()
      .then(response => {
        console.log('UsersPage - Users fetched:', response.data);
        // Use the correct response format from the API
        const userData = response.data.users || [];
        setUsers(Array.isArray(userData) ? userData : []);
        setLoading(false);
      })
      .catch(err => {
        const errorMessage = getApiErrorMessage(err);
        logError('UsersPage - Fetch error', err);
        setError(`Failed to fetch users: ${errorMessage}`);
        setLoading(false);
        
        // Check for auth errors
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          console.error('Authentication error:', err.response.status);
          
          // Clear credentials and redirect to login using authService
          authService.adminLogout();
          navigate('/admin/login');
        }
      });
  }, [adminUser, navigate]);

  if (loading) return <div>Loading users...</div>;
  if (error) return <div className="error-message" style={{ color: 'red', padding: '10px', backgroundColor: '#ffeeee', borderRadius: '4px' }}>{error}</div>;

  return (
    <div>
      <AdminHeader adminUser={adminUser} />
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '20px' 
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '28px', 
          color: '#111827' 
        }}>
          Users
        </h2>
        <p style={{ 
          margin: '0 0 24px 0', 
          fontSize: '14px', 
          color: '#6b7280' 
        }}>
          Manage user accounts and memberships
        </p>

        {users.length === 0 ? (
          <div style={{ 
            padding: '40px', 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px', 
            textAlign: 'center',
            border: '1px solid #e5e7eb'
          }}>
            No users found.
          </div>
        ) : (
          <div style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Name</th>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Email</th>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Org Memberships</th>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Status</th>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Permissions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ 
                      padding: '16px', 
                      fontSize: '14px',
                      color: '#111827',
                      fontWeight: '500'
                    }}>{u.name || '-'}</td>
                    <td style={{ 
                      padding: '16px',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>{u.email}</td>
                    <td style={{ 
                      padding: '16px',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>{Array.isArray(u.memberships) ? u.memberships.join(', ') : '-'}</td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '100px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: (u.status === 'active' || u.isActive) ? '#dcfce7' : '#fee2e2',
                        color: (u.status === 'active' || u.isActive) ? '#166534' : '#dc2626'
                      }}>
                        {u.status || (u.isActive ? 'active' : 'inactive')}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '16px',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>{Array.isArray(u.permissions) ? u.permissions.join(', ') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersPage;
